// src/utils/analyticsDebugger.js
import analyticsService from '@/services/analyticsService';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';

/**
 * Analytics debugging utility to help troubleshoot user property tracking
 */
class AnalyticsDebugger {
    /**
     * Check if analytics is properly initialized
     */
    static checkInitialization() {
        console.group('🔍 Analytics Initialization Check');
        
        console.log('Environment Variables:');
        console.log('- VUE_APP_ENABLE_TRACKING:', import.meta.env.VITE_ENABLE_TRACKING);
        console.log('- VUE_APP_GOOGLE_ANALYTICS_ID:', import.meta.env.VITE_GOOGLE_ANALYTICS_ID);
        
        console.log('\nAnalytics Service State:');
        console.log('- isInitialized:', analyticsService.isInitialized);
        console.log('- analytics instance:', analyticsService.analytics ? '✅ Available' : '❌ Not available');
        console.log('- gtag instance:', analyticsService.gtag ? '✅ Available' : '❌ Not available');
        console.log('- currentUser:', analyticsService.currentUser ? '✅ Set' : '❌ Not set');
        
        console.groupEnd();
    }
    
    /**
     * Check user data from localStorage
     */
    static checkUserData() {
        console.group('👤 User Data Check');
        
        const userData = getUserAddDataFromLocalStorage();
        
        if (userData) {
            console.log('User data found in localStorage:');
            console.log('- UID:', userData.uid);
            console.log('- Email:', userData.email);
            console.log('- User Group:', userData.userGroup);
            console.log('- First Name:', userData.firstName);
            console.log('- Last Name:', userData.lastName);
            console.log('- Timezone:', userData.timezone);
            console.log('- Language:', userData.language);
            console.log('- Partner Types:', userData.partnerTypes);
            console.log('- Roles:', userData.roles);
        } else {
            console.warn('❌ No user data found in localStorage');
        }
        
        console.groupEnd();
    }
    
    /**
     * Test user property setting
     */
    static testUserProperties() {
        console.group('🧪 User Properties Test');
        
        if (!analyticsService.isInitialized) {
            console.error('❌ Analytics service not initialized');
            console.groupEnd();
            return;
        }
        
        const userData = getUserAddDataFromLocalStorage();
        if (!userData) {
            console.error('❌ No user data available');
            console.groupEnd();
            return;
        }
        
        console.log('Testing user property update...');
        analyticsService.updateUserProperties();
        
        console.log('✅ User properties update called');
        console.log('Check Firebase Analytics console for real-time data');
        
        console.groupEnd();
    }
    
    /**
     * Test event tracking - WARNING: This sends REAL events to analytics!
     * Only use this method when you specifically want to test event tracking.
     */
    static testEventTracking() {
        console.group('📊 Event Tracking Test - ⚠️ SENDS REAL EVENTS!');
        
        if (!analyticsService.isInitialized) {
            console.error('❌ Analytics service not initialized');
            console.groupEnd();
            return;
        }
        
        console.warn('⚠️ WARNING: This will send a real debug_test_event to Google Analytics/BigQuery!');
        console.warn('⚠️ Only proceed if you want to test event tracking functionality.');
        
        const testEvent = {
            event_name: 'debug_test_event',
            test_parameter: 'debug_value',
            timestamp: new Date().toISOString()
        };
        
        console.log('Sending test event:', testEvent);
        analyticsService.trackEvent('debug_test_event', {
            test_parameter: 'debug_value',
            debug_session: Date.now()
        });
        
        console.log('✅ Test event sent to analytics');
        console.log('Check Firebase Analytics console for real-time events');
        console.log('This event will appear in BigQuery within 24-48 hours');
        
        console.groupEnd();
    }
    
    /**
     * Run safe analytics diagnostic (no events sent)
     */
    static runSafeDiagnostic() {
        console.group('🔧 Safe Analytics Diagnostic (No Events Sent)');
        console.log('Running analytics check without sending any events...');
        
        this.checkInitialization();
        this.checkUserData();
        this.checkBigQueryRequirements();
        
        console.log('\n📋 Safe Diagnostic Summary:');
        console.log('1. Check console logs above for any errors');
        console.log('2. No events were sent to analytics');
        console.log('3. Use testEventTracking() manually if you need to test event sending');
        console.log('4. Use testUserProperties() manually if you need to test user properties');
        
        console.groupEnd();
    }

    /**
     * Run complete analytics diagnostic - WARNING: Sends real events!
     */
    static runDiagnostic() {
        console.group('🔧 Complete Analytics Diagnostic - ⚠️ SENDS REAL EVENTS!');
        console.warn('⚠️ WARNING: This will send real events to Google Analytics/BigQuery!');
        console.log('Running comprehensive analytics check...');
        
        this.checkInitialization();
        this.checkUserData();
        this.testUserProperties();
        this.testEventTracking();
        
        console.log('\n📋 Diagnostic Summary:');
        console.log('1. Check console logs above for any errors');
        console.log('2. Verify Firebase Analytics real-time events in console');
        console.log('3. Check BigQuery export after 24-48 hours');
        console.log('4. Ensure custom definitions are created in Firebase Console');
        
        console.groupEnd();
    }
    
    /**
     * Check BigQuery export requirements
     */
    static checkBigQueryRequirements() {
        console.group('📊 BigQuery Export Requirements');
        
        console.log('Required Firebase Console Setup:');
        console.log('1. ✅ Analytics → Custom Definitions → Create user properties:');
        console.log('   - user_first_name');
        console.log('   - user_last_name');
        console.log('   - user_timezone');
        console.log('   - user_language');
        console.log('   - user_partner_types');
        console.log('   - user_roles');
        
        console.log('\n2. ✅ Integrations → BigQuery → Enable Analytics export');
        
        console.log('\n3. ✅ Wait 24-48 hours for data to appear in BigQuery');
        
        console.log('\nBigQuery Query Example:');
        console.log(`
SELECT 
  user_id,
  user_properties
FROM \`your-project.analytics_property-id.events_*\`
WHERE event_name = 'page_view'
AND _TABLE_SUFFIX BETWEEN '20250101' AND '20250131'
LIMIT 10
`);
        
        console.groupEnd();
    }
}

// Make it available globally for debugging
if (typeof window !== 'undefined') {
    window.AnalyticsDebugger = AnalyticsDebugger;
}

export default AnalyticsDebugger;